import React, { useEffect, useState } from "react";
import Api from "../../../../shared/api/api";
import AdminSidebar from "../components/AdminSidebar";
import CouponScopeFields from "../components/CouponScopeFields";
import "../styles/AdminCoupons.css";

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // ----- Modal States -----
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  const toggleEarlyAccess = async (coupon) => {
    await Api.updateCoupon(coupon.id, {
        ...coupon,
        allow_early_download: coupon.allow_early_download ? 0 : 1,
    });
    fetchCoupons();
  };
  const emptyForm = {
    code: "",
    description: "",
    discount_type: "fixed",
    amount: "",
    start_date: "",
    end_date: "",
    allow_early_download: false,
    active: true,
    target_book_title: "",
    target_format: "",
    max_uses: "",
  };

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchCoupons();
    Api.getAllAuthor("books").then(data => setBooks(data.books || data || []))
      .catch(() => setError("Could not load books. Refresh before editing a coupon."));
  }, []);

  const fetchCoupons = async () => {
    try {
      const data = await Api.getCoupons();
      setCoupons(data.coupons || []);
    } catch (err) {
      console.error("Coupons load error:", err);
      setError("Could not load coupons.");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ADD
  const saveAdd = async () => {
    await saveCoupon(false);
  };

  const saveCoupon = async (editing) => {
    const free = form.discount_type === "free_ebook";
    if (free && !form.target_book_title) { setError("Select a specific book for a free ebook coupon."); return; }
    const payload = { ...form, target_book_title: form.target_book_title || null,
      max_uses: form.max_uses === "" ? null : Number(form.max_uses),
      target_format: free ? "ebook" : form.target_format || null,
      discount_type: free ? "percent" : form.discount_type, amount: free ? 100 : form.amount };
    setSaving(true); setError("");
    try {
      if (editing) await Api.updateCoupon(selectedCoupon.id, payload);
      else await Api.addCoupon(payload);
      setShowAdd(false); setShowEdit(false); setSelectedCoupon(null); setForm(emptyForm);
      await fetchCoupons();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Could not save coupon.");
    } finally { setSaving(false); }
  };

  // EDIT
  const openEdit = (coupon) => {
    setSelectedCoupon(coupon);
    setForm({
      code: coupon.code,
      description: coupon.description,
      discount_type: coupon.discount_type === "percent" && Number(coupon.amount) === 100 && coupon.target_format === "ebook" ? "free_ebook" : coupon.discount_type,
      amount: coupon.amount,
      start_date: coupon.start_date?.split("T")[0] || "",
      end_date: coupon.end_date?.split("T")[0] || "",
      allow_early_download: coupon.allow_early_download === 1,
      active: coupon.active === 1,
      target_book_title: coupon.target_book_title || "",
      target_format: coupon.target_format || "",
      max_uses: coupon.max_uses ?? "",
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    await saveCoupon(true);
  };

  // DELETE
  const openDelete = (coupon) => {
    setSelectedCoupon(coupon);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    await Api.deleteCoupon(selectedCoupon.id);
    setShowDelete(false);
    setSelectedCoupon(null);
    fetchCoupons();
  };

  // TOGGLE ACTIVE
  const toggleActive = async (id) => {
    await Api.toggleCoupon(id);
    fetchCoupons();
  };

  return (
    <>
    <AdminSidebar />
    <div className="adminCouponsContainer">
      <h1 className="couponHeader">Coupons</h1>

      <button className="addBtn" onClick={() => { setForm(emptyForm); setError(""); setShowAdd(true); }}>
        Add Coupon
      </button>
      {error && !showAdd && !showEdit && <p role="alert">{error}</p>}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="couponTableScroll"><table className="couponTable">
          <thead>
            <tr>
              <th>Code</th>
              <th>Type</th>
              <th>Value</th>
              <th>Book</th>
              <th>Edition</th>
              <th>Uses</th>
              <th>Early Access</th>
              <th>Active</th>
              <th>Start</th>
              <th>End</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {coupons.map((c) => (
              <tr key={c.id}>
                <td>{c.code}</td>
                <td>{c.discount_type}</td>
                <td>
                  {c.discount_type === "percent"
                    ? `${c.amount}%`
                    : `$${c.amount}`}
                </td>
                <td>{c.target_book_title || (c.target_book_id != null ? "Invalid legacy book restriction" : "All books")}</td>
                <td>{c.target_format || "All editions"}</td>
                <td>{Number(c.uses_count || 0)} / {c.max_uses ?? "Unlimited"}</td>
                <td className={`statusCell`}>
                     {/* EARLY ACCESS STATUS */}
                    <button
                        className={`earlyAccessBtn ${c.allow_early_download ? "earlyYes" : "earlyNo"}`}
                        onClick={() => toggleEarlyAccess(c)}
                        style={{ marginLeft: "8px" }}
                    >
                        {c.allow_early_download ? "Early" : "No Early"}
                    </button>
                </td>

                {/* ACTIVE TOGGLE BUTTON */}
                <td>
                  <button
                    className={`statusBtn ${c.active ? "activeStatus" : "inactiveStatus"}`}
                    onClick={() => toggleActive(c.id)}
                  >
                    {c.active ? "Active" : "Inactive"}
                  </button>
                </td>

                <td>{c.start_date?.split("T")[0] || "—"}</td>
                <td>{c.end_date?.split("T")[0] || "—"}</td>

                <td className="actionsCell">
                  <button className="editBtn" onClick={() => openEdit(c)}>
                    Edit
                  </button>
                  <button className="deleteBtn" onClick={() => openDelete(c)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {/* ===== MODALS BELOW — ADD / EDIT / DELETE ===== */}

      {showAdd && (
        <div className="modalOverlay">
          <div className="modalBox" role="dialog" aria-modal="true" aria-label="Add Coupon">
            <h2>Add Coupon</h2>
            {error && <p role="alert">{error}</p>}

            <div className="modalForm">
              <label htmlFor="coupon-code">Code</label>
              <input
                id="coupon-code"
                type="text"
                value={form.code}
                onChange={(e) => updateField("code", e.target.value)}
              />

              <label>Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
              />

              <CouponScopeFields form={form} books={books} updateField={updateField} />
              <label htmlFor="coupon-type">Type</label>
              <select
                id="coupon-type"
                value={form.discount_type}
                onChange={(e) => updateField("discount_type", e.target.value)}
              >
                <option value="fixed">Fixed</option>
                <option value="percent">Percent</option>
                <option value="free_ebook">Free ebook</option>
              </select>

              <label htmlFor="coupon-amount">Amount</label>
              <input
                id="coupon-amount"
                type="number"
                value={form.discount_type === "free_ebook" ? 100 : form.amount}
                disabled={form.discount_type === "free_ebook"}
                min="0.01" step="0.01"
                onChange={(e) => updateField("amount", e.target.value)}
              />

              <label>Start Date</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => updateField("start_date", e.target.value)}
              />

              <label>End Date</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => updateField("end_date", e.target.value)}
              />

              <label>
                <input
                  type="checkbox"
                  checked={form.allow_early_download}
                  onChange={(e) =>
                    updateField("allow_early_download", e.target.checked)
                  }
                />
                Allow Early Download
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => updateField("active", e.target.checked)}
                />
                Active
              </label>
            </div>

            <div className="modalActions">
              <button className="saveBtn" onClick={saveAdd} disabled={saving}>
                Save
              </button>
              <button className="cancelBtn" onClick={() => setShowAdd(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showEdit && (
        <div className="modalOverlay">
          <div className="modalBox" role="dialog" aria-modal="true" aria-label="Edit Coupon">
            <h2>Edit Coupon</h2>
            {error && <p role="alert">{error}</p>}

            <div className="modalForm">
              <label htmlFor="coupon-code">Code</label>
              <input
                id="coupon-code"
                type="text"
                value={form.code}
                onChange={(e) => updateField("code", e.target.value)}
              />

              <label>Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
              />

              <CouponScopeFields form={form} books={books} updateField={updateField} />
              <label htmlFor="coupon-type">Type</label>
              <select
                id="coupon-type"
                value={form.discount_type}
                onChange={(e) => updateField("discount_type", e.target.value)}
              >
                <option value="fixed">Fixed</option>
                <option value="percent">Percent</option>
                <option value="free_ebook">Free ebook</option>
              </select>

              <label htmlFor="coupon-amount">Amount</label>
              <input
                id="coupon-amount"
                type="number"
                value={form.discount_type === "free_ebook" ? 100 : form.amount}
                disabled={form.discount_type === "free_ebook"}
                min="0.01" step="0.01"
                onChange={(e) => updateField("amount", e.target.value)}
              />

              <label>Start Date</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => updateField("start_date", e.target.value)}
              />

              <label>End Date</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => updateField("end_date", e.target.value)}
              />

              <label>
                <input
                  type="checkbox"
                  checked={form.allow_early_download}
                  onChange={(e) =>
                    updateField("allow_early_download", e.target.checked)
                  }
                />
                Allow Early Download
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => updateField("active", e.target.checked)}
                />
                Active
              </label>
            </div>

            <div className="modalActions">
              <button className="saveBtn" onClick={saveEdit} disabled={saving}>
                Save
              </button>
              <button className="cancelBtn" onClick={() => setShowEdit(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDelete && (
        <div className="modalOverlay">
          <div className="modalBox deleteBox">
            <h2>Delete Coupon</h2>
            <p>
              Are you sure you want to delete{" "}
              <strong>{selectedCoupon.code}</strong>?
            </p>

            <div className="modalActions">
              <button className="deleteConfirmBtn" onClick={confirmDelete}>
                Delete
              </button>
              <button className="cancelBtn" onClick={() => setShowDelete(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
